import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useApi } from '../../hooks/useApi';

function todayYmd() { return new Date().toISOString().slice(0, 10); }
function plusDays(ymd, days) {
  const d = new Date(ymd + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function isPastDue(ymd) { return ymd && ymd < todayYmd(); }

function eventTypeChip(type) {
  switch (type) {
    case 'audit-notification':   return <Chip size="small" label="Audit notify"   color="warning" variant="outlined"/>;
    case 'meeting-notification': return <Chip size="small" label="Meeting notify" color="info"    variant="outlined"/>;
    case 'qbr-meeting':          return <Chip size="small" label="QBR meeting"    color="primary"/>;
    default:                     return <Chip size="small" label={type}/>;
  }
}

function Calendar() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // Default window: today through +90 days
  const [start, setStart] = useState(todayYmd());
  const [end,   setEnd]   = useState(plusDays(todayYmd(), 90));

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const q = [];
      if (start) q.push(`start=${encodeURIComponent(start)}`);
      if (end)   q.push(`end=${encodeURIComponent(end)}`);
      const url = `cma/qbrcycle/calendar${q.length ? '?' + q.join('&') : ''}`;
      const response = await jApi.request('GET', url);
      setEvents(Array.isArray(response.data) ? response.data : []);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load calendar');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [start, end]);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/qbr" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>QBR</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Calendar</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  QBR Calendar
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isLoading ? 'Loading…' : `${events.length} event${events.length === 1 ? '' : 's'} in window`}
                </Typography>
              </Box>
              <TextField label="Start" type="date" size="small" value={start} onChange={e => setStart(e.target.value)} InputLabelProps={{ shrink: true }}/>
              <TextField label="End"   type="date" size="small" value={end}   onChange={e => setEnd(e.target.value)}   InputLabelProps={{ shrink: true }}/>
              <IconButton size="small" onClick={fetchData} disabled={isLoading}>
                <RefreshIcon/>
              </IconButton>
            </Stack>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {!isLoading && events.length === 0 && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No QBR events in this window.
            </Typography>
          </Paper>
        )}

        {!isLoading && events.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Cycle</TableCell>
                  <TableCell>Action Required</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e, idx) => (
                  <TableRow key={`${e.qbrcycle_id}-${e.event_type}-${idx}`} hover sx={{ cursor: 'pointer',
                      ...(isPastDue(e.date) && { '& td': { color: 'error.main', fontWeight: 700 } })
                    }}
                    onClick={() => navigate(`/qbr/cycle/${e.qbrcycle_id}`)}>
                    <TableCell>{e.date}</TableCell>
                    <TableCell>{eventTypeChip(e.event_type)}</TableCell>
                    <TableCell>{e.customer}</TableCell>
                    <TableCell><code>{e.qbr_cycle}</code></TableCell>
                    <TableCell>{e.action_required}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
}

export default Calendar;
